import { Box, Dialog, DialogActions, DialogContent, Stack, Typography } from '@mui/material';
import { MprcButton, MprcDialogTitleClose, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator } from 'components';
import { ButtonCancel, ButtonSave } from 'components/buttons';
import { ImageSelectionResult, MprcImageSelectionDialog } from 'components/MprcImageHandler';
import { FileGetDto } from 'generated/CoreApi_v1';
import MediaUtils from 'lib/MediaUtils';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    publicationId: string;
    attachId: string;
    title: string;
    onClose: (success: boolean) => void;
}

////////////////////////////////////////////////////////////////////////////////

export const UpdatePosterDialog: React.FC<IProps> = (props) => {
    const { publicationId, attachId, title, onClose } = props;

    const { t } = useTranslation("knowledge");
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();

    const [poster, setPoster] = useState<string | null>(null);
    const [dialog, setDialog] = useState<any>(null);
    const [viewData, setViewData] = useState<FileGetDto | null>(null);
    const [viewError, setViewError] = useState<any>(null);
    const [viewErrorLoading, setViewErrorLoading] = useState<any>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);

    const dimension = useMemo(() => {
        const isVertical = viewData != null && viewData.metadata.width && viewData.metadata.height && viewData.metadata.width < viewData.metadata.height;
        const dimension: ImageDimension = isVertical
            ? { width: 1080, height: 1920 }
            : { width: 1920, height: 1080 };
        return dimension;
    }, [viewData]);

    ///
    const handleSave = async () => {
        setSaving(true);

        try {
            var result = await api.v1.knowledgePublication.updateMediaPoster({
                publicationId,
                attachId,
                posterBase64: poster!,
                width: dimension.width,
                height: dimension.height
            });

            if (result.data) {
                enqueueSnackbar(t("Feed.Card.Actions.UpdatePosterDialog.UpdateSuccessMessage"), { variant: "success" });
                onClose(true);
            }
            else {
                enqueueSnackbar(t("Feed.Card.Actions.UpdatePosterDialog.UpdateErrorMessage"), { variant: "error" });
            }

        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setSaving(false);
        }
    };

    ///
    const doLoadImage = (image: ImageSelectionResult | null) => {
        if (image != null) {
            var finalImage = image.editedImage ?? image.source;
            setPoster(finalImage.sourceDataOrUrl);
        }
        setDialog(null);
    };

    ///
    const handleImageSelection = () => {

        setDialog(
            <MprcImageSelectionDialog
                acceptedFiles={MediaUtils.DropzoneAcceptImages()}
                fitMode='cover'
                initialSource="library"
                backgroundType="solid"
                allowEdit={true}
                dimension={dimension}
                onClose={doLoadImage}
            />
        );
    };

    ///
    const RenderPoster = () => {
        if (viewData == null) return null;

        const isVertical = viewData.metadata.width && viewData.metadata.height && viewData.metadata.width < viewData.metadata.height;

        return (
            <Box sx={{ display: "flex", maxWidth: "100%", maxHeight: 315 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: "100%", overflow: "hidden" }}>
                    <Box sx={{ flex: 1, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <img
                            src={poster ?? viewData.posterUrl ?? ""}
                            style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                width: "auto",
                                height: "auto",
                                objectFit: isVertical ? "contain" : "cover"
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="caption">{t("Feed.Card.Actions.UpdatePosterDialog.UpdateHint")}</Typography>
                    </Box>
                </Box>
            </Box>
        );
    };

    ///
    useEffect(() => {
        async function load() {
            setLoading(true);
            try {
                var result = await api.v1.knowledgePublication.getAttachFile({ attachId });
                setViewData(result.data);
            }
            catch (e) {
                setViewErrorLoading(e);
            }
            finally {
                setLoading(false);
            }
        }

        load();
    }, []);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (<Dialog
        open
        onClose={() => onClose(false)}
        maxWidth="sm"
        fullWidth
    >
        <MprcDialogTitleClose
            title={t("Feed.Card.Actions.UpdatePosterDialog.Title")}
            subtitle={title}
            onClose={() => onClose(false)}
            shrinkPadding
        />
        <DialogContent>
            <MprcRequestIndicator type="request" visible={loading} />
            <MprcRequestIndicator type="post" visible={saving} message={t("Feed.Card.Actions.UpdatePosterDialog.SendingImage")} />

            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

            {dialog}

            {
                viewErrorLoading
                    ? <MprcRequestErrorReport error={viewErrorLoading} />
                    : RenderPoster()
            }
        </DialogContent>
        <DialogActions>
            <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" gap={2}>
                {
                    viewErrorLoading
                        ? <div></div>
                        : <MprcButton text={t("Feed.Card.Actions.UpdatePosterDialog.SelectButtonText")} variant="contained" color="primary" onClick={handleImageSelection} />
                }
                <Stack direction="row" gap={2}>
                    <ButtonCancel onClick={() => onClose(false)} />
                    {
                        !viewErrorLoading &&
                        <ButtonSave disabled={poster == null} onClick={handleSave} />
                    }
                </Stack>
            </Stack>
        </DialogActions>
    </Dialog>);
};
